/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { motion } from "framer-motion";
import AgreementPage from "@/AgreementPage";
import { AdminLoginPage, AdminDashboard } from "@/admin/AdminApp";
import { QuickLinks } from "@/components/QuickLinks";
import { RulesPillLink, RulesModalTrigger, RulesDialog } from "@/components/RulesModal";
import { OtherWaysToGive } from "@/components/OtherWaysToGive";
import PageLayout from "@/components/PageLayout";
import RulesPage from "@/pages/RulesPage";
import {
  Crown,
  Sparkles,
  Heart,
  BookOpen,
  Users,
  Target,
  Star,
  Phone,
  Mail,
  Instagram,
  Facebook,
  ArrowRight,
  Check,
  Menu,
  X,
  Gift,
  HeartHandshake,
  Hourglass,
  GraduationCap,
  Handshake,
  Plus,
  Key,
  Globe,
  Scroll,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IMG = {
  hero: "https://images.unsplash.com/photo-1607748862156-7c548e7e98f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGdpcmxzJTIwZ3JvdXB8ZW58MHx8fHwxNzgxNTg0MDE4fDA&ixlib=rb-4.1.0&q=85",
  founder: "/lacey-fincher.jpg",
  royal:
    "https://images.unsplash.com/photo-1624272949900-9ae4c56397e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxzbWlsaW5nJTIwbGl0dGxlJTIwZ2lybHxlbnwwfHx8fDE3ODE1ODQwMjV8MA&ixlib=rb-4.1.0&q=85",
  bold: "/tier-bold-queens.jpg",
  fearless: "/tier-fearless-queens.jpg",
};

// Inline butterfly SVG (lucide doesn't ship one)
function ButterflyIcon({ className, style }) {
  return (
    <svg viewBox="0 0 24 24" className={className} style={style} fill="currentColor" aria-hidden>
      <path d="M12 4c-.6.9-1.6 2.6-3.5 3.2C6 8 3 6.5 2.5 9c-.5 2.5 1 4 3 4.5 0 0-2 .8-2 2.8 0 1.4 1.1 2.7 2.7 2.7 2.4 0 4.3-1.9 5.3-4.5l.5-1.5.5 1.5c1 2.6 2.9 4.5 5.3 4.5 1.6 0 2.7-1.3 2.7-2.7 0-2-2-2.8-2-2.8 2-.5 3.5-2 3-4.5C21 6.5 18 8 15.5 7.2 13.6 6.6 12.6 4.9 12 4z" />
      <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="0.6" fill="none" />
    </svg>
  );
}

const TIERS = [
  {
    id: "royal_crowns",
    name: "Royal Crowns",
    range: "Ages 6 – 10",
    symbol: Key,
    color: "#ff6fa5",
    soft: "rgba(255, 111, 165, 0.12)",
    border: "rgba(255, 111, 165, 0.45)",
    desc: "Building self-esteem, confidence, and positive habits through fun activities, friendship, and faith.",
    img: IMG.royal,
    bullets: ["Confidence-building games", "Friendship & kindness", "Faith & gratitude"],
  },
  {
    id: "bold_queens",
    name: "Bold Queenz",
    range: "Ages 11 – 14",
    symbol: ButterflyIcon,
    color: "#9b59b6",
    soft: "rgba(155, 89, 182, 0.12)",
    border: "rgba(155, 89, 182, 0.5)",
    desc: "Developing leadership skills, identity, healthy choices, and strong relationships.",
    img: IMG.bold,
    bullets: ["Leadership labs", "Identity & self-worth", "Healthy boundaries"],
  },
  {
    id: "fearless_queens",
    name: "Fearless Queenz",
    range: "Ages 15 – 18",
    symbol: Crown,
    color: "#e89a2a",
    soft: "rgba(232, 154, 42, 0.13)",
    border: "rgba(232, 154, 42, 0.5)",
    desc: "Preparing for the future — goal setting, financial literacy, mentorship, and becoming change-makers.",
    img: IMG.fearless,
    bullets: ["Goal & vision boards", "Financial literacy", "Career & college prep"],
  },
];

const FOCUS = [
  { icon: Heart, label: "Self-Esteem & Confidence", color: "#d62288" },
  { icon: Crown, label: "Leadership Development", color: "#7d289b" },
  { icon: BookOpen, label: "Life Skills & Education", color: "#d4af37" },
  { icon: Users, label: "Positive Relationships", color: "#d62288" },
  { icon: Star, label: "Faith & Character", color: "#7d289b" },
  { icon: Target, label: "Goal Setting & Success", color: "#d4af37" },
];

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  }),
};

/* ------------ Navigation ------------ */
function Nav() {
  const [open, setOpen] = useState(false);
  const links = [
    { href: "/#mission", label: "Mission", anchor: true },
    { href: "/#about", label: "About", anchor: true },
    { href: "/#programs", label: "Programs", anchor: true },
    { href: "/sponsor", label: "Sponsor", anchor: false },
    { href: "/waitlist", label: "Waitlist", anchor: false },
    { href: "/register", label: "Register", anchor: false },
    { href: "/rules", label: "Rules", anchor: false },
  ];

  return (
    <header
      data-testid="site-nav"
      className="sticky top-0 z-50 w-full bg-white/85 backdrop-blur-md border-b border-[#f0e1e8]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-2.5 group"
        >
          <img
            src="/logo-sm.png"
            alt="Queenz Like Me"
            className="h-11 w-11 rounded-full object-cover shadow-md ring-2 ring-[#d4af37]/50 group-hover:ring-[#d4af37] transition-all"
          />
          <div className="leading-none hidden sm:block">
            <div className="flex items-baseline gap-1">
              <span className="font-script text-2xl gold-text">
                Queen<span className="font-display not-italic">z</span>
              </span>
              <span className="font-display text-base font-semibold text-[#7d289b]">
                Like Me
              </span>
            </div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#d62288] font-semibold mt-0.5">
              Girls Mentoring
            </p>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-6">
          {links.map((l) =>
            l.anchor ? (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-sm font-medium text-[#5c4a54] hover:text-[#d62288] transition-colors relative after:content-[''] after:absolute after:-bottom-1 after:left-0 after:w-0 after:h-[2px] after:bg-[#d4af37] after:transition-all hover:after:w-full"
              >
                {l.label}
              </a>
            ) : (
              <Link
                key={l.href}
                to={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-sm font-medium text-[#5c4a54] hover:text-[#d62288] transition-colors relative after:content-[''] after:absolute after:-bottom-1 after:left-0 after:w-0 after:h-[2px] after:bg-[#d4af37] after:transition-all hover:after:w-full"
              >
                {l.label}
              </Link>
            )
          )}
        </nav>

        <Link
          to="/register"
          data-testid="nav-register-cta"
          className="hidden lg:inline-flex btn-primary text-sm py-2.5 px-5"
        >
          Join the Sisterhood
          <ArrowRight className="h-4 w-4" />
        </Link>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-[#7d289b]"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden border-t border-[#f0e1e8] bg-white/95 backdrop-blur"
        >
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map((l) =>
              l.anchor ? (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-base font-medium text-[#5c4a54] py-2"
                >
                  {l.label}
                </a>
              ) : (
                <Link
                  key={l.href}
                  to={l.href}
                  onClick={() => setOpen(false)}
                  className="text-base font-medium text-[#5c4a54] py-2"
                >
                  {l.label}
                </Link>
              )
            )}
            <Link
              to="/register"
              onClick={() => setOpen(false)}
              className="btn-primary justify-center mt-2"
            >
              Join the Sisterhood <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

/* ------------ Hero ------------ */
function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden royal-gradient"
    >
      {/* floating sparkles */}
      <Sparkles
        className="absolute top-24 left-[8%] h-6 w-6 text-[#d4af37] floaty opacity-70"
        aria-hidden
      />
      <Sparkles
        className="absolute top-40 right-[12%] h-8 w-8 text-[#d62288] floaty-slow opacity-60"
        aria-hidden
      />
      <Crown
        className="absolute bottom-16 left-[15%] h-7 w-7 text-[#7d289b] floaty-slow opacity-40"
        aria-hidden
      />
      <Heart
        className="absolute bottom-32 right-[8%] h-6 w-6 text-[#d62288] floaty opacity-60"
        aria-hidden
      />

      <div className="max-w-5xl mx-auto px-6 lg:px-8 pt-12 pb-24 lg:pt-16 lg:pb-32 text-center">
        {/* Official brand logo — front and center, first thing visitors see */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative inline-block"
        >
          {/* Glow behind logo */}
          <div
            className="absolute inset-0 rounded-full blur-3xl opacity-70 scale-110"
            style={{
              background:
                "radial-gradient(circle, rgba(214,34,136,0.55) 0%, rgba(212,175,55,0.4) 40%, transparent 70%)",
            }}
            aria-hidden
          />
          <img
            src="/logo-lg.png"
            alt="Queenz Like Me — official logo"
            data-testid="hero-logo"
            className="relative w-72 sm:w-96 lg:w-[28rem] mx-auto rounded-full shadow-[0_30px_80px_-20px_rgba(214,34,136,0.6)] ring-4 ring-[#d4af37]/40 floaty-slow"
          />
          {/* gold ribbon */}
          <div className="absolute -top-2 -right-4 sm:-right-6 bg-gradient-to-br from-[#f6e27a] to-[#d4af37] text-[#4a1466] font-display font-bold px-4 sm:px-5 py-1.5 sm:py-2 rounded-full shadow-lg rotate-[8deg] text-xs sm:text-sm z-10">
            ♕ Est. with love
          </div>
        </motion.div>

        <motion.div
          initial="hidden"
          animate="show"
          variants={fade}
          className="mt-10"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/70 backdrop-blur border border-[#f0d4e3] text-xs font-semibold tracking-wider text-[#7d289b] uppercase">
            <Crown className="h-3.5 w-3.5 text-[#d4af37]" />
            Girls Mentoring Program · Ages 6 – 18
          </div>

          <h1 className="mt-6 font-display text-[2.4rem] sm:text-6xl lg:text-7xl font-bold leading-[1.04] tracking-tight text-[#1a0f14]">
            We don't just{" "}
            <span className="magenta-text">mentor girls</span>,
            <br />
            we create{" "}
            <span className="font-script gold-text shimmer-gold text-[3rem] sm:text-7xl lg:text-8xl leading-none">
              Queen<span className="font-display not-italic font-bold">z</span>
            </span>
            .
          </h1>

          <p className="mt-7 text-lg sm:text-xl text-[#5c4a54] leading-relaxed max-w-2xl mx-auto">
            A safe, supportive sisterhood where girls grow emotionally, socially,
            and spiritually — discovering their worth and walking boldly in their
            purpose.
          </p>

          <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/register"
              data-testid="hero-register-cta"
              className="btn-primary text-base"
            >
              Register Today
              <ArrowRight className="h-5 w-5" />
            </Link>
            <a
              href="#programs"
              data-testid="hero-programs-cta"
              className="btn-outline-gold text-base"
            >
              Explore the Programs
            </a>
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-[#5c4a54]">
            {[
              "Confident & Courageous",
              "Faith & Character",
              "Tomorrow's Leaders",
            ].map((t) => (
              <span key={t} className="inline-flex items-center gap-2">
                <Check className="h-4 w-4 text-[#d4af37]" />
                <span className="font-medium">{t}</span>
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ------------ Mission ------------ */
function Mission() {
  return (
    <section
      id="mission"
      data-testid="mission-section"
      className="relative py-24 sm:py-32 overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, #2d0a40 0%, #4a1466 35%, #7d289b 75%, #d62288 100%)",
      }}
    >
      {/* Decorative elements */}
      <div className="absolute inset-0 gold-dots opacity-25 pointer-events-none" aria-hidden />
      <Sparkles
        className="absolute top-16 left-[6%] h-10 w-10 text-[#f6e27a]/40 floaty"
        aria-hidden
      />
      <Sparkles
        className="absolute bottom-20 right-[8%] h-9 w-9 text-[#f6e27a]/35 floaty-slow"
        aria-hidden
      />
      <Crown
        className="absolute top-1/2 right-[5%] h-14 w-14 text-[#f6e27a]/20 floaty-slow"
        aria-hidden
      />

      <div className="max-w-5xl mx-auto px-6 lg:px-8 relative">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={fade}
          className="text-center"
        >
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#f6e27a]/15 border border-[#f6e27a]/40 mb-6">
            <Crown className="h-7 w-7 text-[#f6e27a]" />
          </div>
          <p className="font-script text-3xl sm:text-4xl text-[#f6e27a] mb-2">
            Our Mission
          </p>
          <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-tight">
            When a girl knows who she is,
            <br />
            <span className="font-script gold-text shimmer-gold text-5xl sm:text-7xl lg:text-8xl leading-none">
              she becomes unstoppable.
            </span>
          </h2>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-2 gap-7">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            custom={0}
            variants={fade}
            data-testid="mission-statement-1"
            className="relative rounded-3xl p-8 sm:p-10 bg-white/10 backdrop-blur-md border border-white/20"
          >
            <div className="absolute -top-4 -left-4 h-10 w-10 rounded-full bg-[#f6e27a] flex items-center justify-center shadow-lg">
              <Crown className="h-5 w-5 text-[#4a1466]" />
            </div>
            <p className="text-white/95 text-base sm:text-lg leading-relaxed">
              <span className="font-display font-semibold text-[#f6e27a]">
                Queenz Like Me
              </span>{" "}
              is dedicated to empowering girls ages 6–18 to know their worth,
              find their voice, and walk confidently in their purpose. We
              provide mentorship, leadership development, life skills, and a
              strong sisterhood where every girl is{" "}
              <span className="text-[#f6e27a] font-semibold">seen, heard, valued,</span>{" "}
              and challenged to become the best version of herself.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            custom={1}
            variants={fade}
            data-testid="mission-statement-2"
            className="relative rounded-3xl p-8 sm:p-10 bg-white/10 backdrop-blur-md border border-white/20"
          >
            <div className="absolute -top-4 -left-4 h-10 w-10 rounded-full bg-[#f6e27a] flex items-center justify-center shadow-lg">
              <Sparkles className="h-5 w-5 text-[#4a1466]" />
            </div>
            <p className="text-white/95 text-base sm:text-lg leading-relaxed">
              We are building confident, resilient, and purpose-driven young
              women who lead with{" "}
              <span className="text-[#f6e27a] font-semibold">
                character, courage, and compassion.
              </span>{" "}
              Our mission is to equip every queen with the tools to overcome
              obstacles, make positive choices, and create lasting impact —
              because when a girl knows who she is,{" "}
              <span className="font-display italic text-[#f6e27a]">
                she becomes unstoppable.
              </span>
            </p>
          </motion.div>
        </div>

        {/* Three value pills */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={fade}
          className="mt-12 flex flex-wrap justify-center gap-3"
        >
          {["Character", "Courage", "Compassion"].map((v) => (
            <span
              key={v}
              data-testid={`mission-value-${v.toLowerCase()}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#f6e27a]/15 border border-[#f6e27a]/40 text-[#f6e27a] font-display font-semibold tracking-wide"
            >
              <Crown className="h-4 w-4" />
              {v}
            </span>
          ))}
        </motion.div>

        <div className="mt-12 text-center">
          <Link
            to="/register"
            data-testid="mission-cta"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#f6e27a] text-[#4a1466] font-semibold hover:bg-white transition-colors shadow-xl"
          >
            Join the Sisterhood
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ------------ About ------------ */
function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 sm:py-32 about-deep-bg overflow-hidden"
    >
      {/* Decorative gold dots overlay */}
      <div className="absolute inset-0 gold-dots opacity-60 pointer-events-none" aria-hidden />
      <Sparkles className="absolute top-16 right-[8%] h-9 w-9 text-[#d4af37] floaty opacity-80" aria-hidden />
      <Crown className="absolute bottom-20 left-[6%] h-10 w-10 text-[#7d289b] floaty-slow opacity-50" aria-hidden />
      <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center relative">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          variants={fade}
        >
          <div className="crown-divider mb-6">
            <Crown className="h-5 w-5" />
          </div>
          <p className="font-script text-3xl gold-text mb-2">About</p>
          <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-[#4a1466]">
            Inspiring her to know her worth.
          </h2>
          <p className="mt-7 text-lg sm:text-xl text-[#5c4a54] leading-relaxed">
            <span className="font-display font-semibold text-[#d62288]">
              Queenz Like Me
            </span>{" "}
            is a mentoring program dedicated to inspiring and empowering girls
            to know their worth, discover their purpose, and walk boldly in
            their destiny. We provide a safe and supportive space where girls
            grow emotionally, socially, and spiritually while building lifelong
            friendships and leadership skills.
          </p>
        </motion.div>

        <div className="mt-14 grid sm:grid-cols-3 gap-6">
          {[
            { n: "3", l: "Age-tailored programs" },
            { n: "6", l: "Core focus pillars" },
            { n: "∞", l: "Sisterhood for life" },
          ].map((s, i) => (
            <motion.div
              key={s.l}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              custom={i}
              variants={fade}
              className="glass-card rounded-2xl p-6"
            >
              <div className="font-display text-5xl font-bold gold-text">
                {s.n}
              </div>
              <p className="mt-2 text-sm font-medium text-[#5c4a54] tracking-wide uppercase">
                {s.l}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------ Programs / Tiers ------------ */
function Programs() {
  return (
    <section
      id="programs"
      data-testid="programs-section"
      className="relative py-24 sm:py-32 programs-bg overflow-hidden"
    >
      <Sparkles className="absolute top-20 left-[5%] h-8 w-8 text-[#d4af37] floaty opacity-60" aria-hidden />
      <Sparkles className="absolute bottom-32 right-[8%] h-10 w-10 text-[#d62288] floaty-slow opacity-40" aria-hidden />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="text-center max-w-3xl mx-auto">
          <div className="crown-divider mb-6">
            <Crown className="h-5 w-5" />
          </div>
          <p className="font-script text-3xl text-[#7a5a14] drop-shadow-[0_1px_0_rgba(255,255,255,0.6)] mb-2">Three Groups</p>
          <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-[#4a1466]">
            Empowering girls in every season.
          </h2>
          <p className="mt-5 text-lg text-[#5c4a54]">
            Each group is thoughtfully designed for where she is — and where
            she's going.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-7">
          {TIERS.map((t, i) => (
            <motion.div
              key={t.id}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              custom={i}
              variants={fade}
              data-testid={`tier-card-${t.id}`}
              className="group relative rounded-3xl overflow-hidden border-2 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl"
              style={{
                borderColor: t.border,
                background: `linear-gradient(180deg, white 0%, ${t.soft} 100%)`,
              }}
            >
              <div className="relative aspect-[5/4] overflow-hidden">
                <img
                  src={t.img}
                  alt={t.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background: `linear-gradient(180deg, transparent 40%, ${t.color}cc 100%)`,
                  }}
                />
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/85 backdrop-blur">
                  <Crown className="h-4 w-4" style={{ color: t.color }} />
                  <span
                    className="text-xs font-bold uppercase tracking-wider"
                    style={{ color: t.color }}
                  >
                    {t.range}
                  </span>
                </div>
                <h3 className="absolute bottom-5 left-5 right-5 font-display text-3xl font-bold text-white drop-shadow-md flex items-center gap-2">
                  {t.name}
                  <t.symbol className="h-7 w-7 text-[#f6e27a] drop-shadow-md" />
                </h3>
              </div>

              <div className="p-7">
                <p className="text-[#1a0f14] font-medium leading-relaxed">{t.desc}</p>
                <ul className="mt-5 space-y-2.5">
                  {t.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-2.5 text-sm text-[#1a0f14]"
                    >
                      <Check
                        className="h-4 w-4 mt-0.5 shrink-0"
                        style={{ color: t.color }}
                      />
                      <span className="font-medium">{b}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/register"
                  data-testid={`tier-cta-${t.id}`}
                  className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold transition-colors"
                  style={{ color: t.color }}
                >
                  Register for {t.name}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------ Focus pillars ------------ */
function Focus() {
  return (
    <section
      id="focus"
      data-testid="focus-section"
      className="relative py-24 sm:py-32 gold-bg overflow-hidden"
    >
      <div className="absolute inset-0 gold-dots opacity-40 pointer-events-none" aria-hidden />
      <Crown className="absolute top-16 right-[7%] h-10 w-10 text-[#d4af37] floaty opacity-50" aria-hidden />
      <Heart className="absolute bottom-24 left-[6%] h-7 w-7 text-[#d62288] floaty-slow opacity-50" aria-hidden />
      <div className="max-w-6xl mx-auto px-6 lg:px-8 relative">
        <div className="text-center max-w-3xl mx-auto">
          <div className="crown-divider mb-6">
            <Crown className="h-5 w-5" />
          </div>
          <p className="font-script text-3xl text-[#7a5a14] drop-shadow-[0_1px_0_rgba(255,255,255,0.6)] mb-2">We Focus On</p>
          <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-[#4a1466]">
            Six pillars. One purpose.
          </h2>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FOCUS.map((f, i) => (
            <motion.div
              key={f.label}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              custom={i}
              variants={fade}
              data-testid={`focus-${i}`}
              className="glass-card rounded-2xl p-6 flex items-start gap-4 hover:-translate-y-1 transition-transform"
            >
              <div
                className="h-12 w-12 rounded-2xl flex items-center justify-center shrink-0"
                style={{
                  background: `${f.color}1f`,
                  border: `1.5px solid ${f.color}55`,
                }}
              >
                <f.icon className="h-6 w-6" style={{ color: f.color }} />
              </div>
              <div>
                <h3 className="font-display text-lg font-semibold text-[#1a0f14]">
                  {f.label}
                </h3>
                <p className="mt-1 text-sm text-[#5c4a54] leading-relaxed">
                  {pillarBlurb(f.label)}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function pillarBlurb(label) {
  const map = {
    "Self-Esteem & Confidence":
      "Helping every girl recognize her worth and walk in it daily.",
    "Leadership Development":
      "Voice, vision, and the skills to lead with grace.",
    "Life Skills & Education":
      "Practical tools for school, home, and the real world.",
    "Positive Relationships":
      "Sisterhood, healthy boundaries, and lifelong friendships.",
    "Faith & Character":
      "Grounded in values, kindness, gratitude, and integrity.",
    "Goal Setting & Success":
      "Dreaming big, planning smart, and making it happen.",
  };
  return map[label] || "";
}

/* ------------ Founder ------------ */
function Founder() {
  return (
    <section
      id="founder"
      data-testid="founder-section"
      className="relative py-24 sm:py-32 overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, #4a1466 0%, #7d289b 60%, #d62288 100%)",
      }}
    >
      <Sparkles className="absolute top-12 right-[10%] h-8 w-8 text-[#f6e27a]/40 floaty" />
      <Crown className="absolute bottom-12 left-[8%] h-10 w-10 text-[#f6e27a]/30 floaty-slow" />

      <div className="max-w-6xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center relative">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5"
        >
          <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden border-4 border-[#f6e27a]/70 shadow-2xl">
            <img
              src={IMG.founder}
              alt="Lacey Fincher — Founder of Queenz Like Me"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute -bottom-px left-0 right-0 h-20 bg-gradient-to-t from-[#4a1466] to-transparent" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-7 text-white"
        >
          <p className="font-script text-3xl text-[#f6e27a] mb-2">
            Meet the Founder
          </p>
          <h2 className="font-display text-4xl sm:text-5xl font-bold leading-tight">
            Lacey Fincher
          </h2>
          <div className="mt-3 inline-flex items-center gap-2 text-sm font-semibold tracking-wider uppercase text-[#f6e27a]">
            <Crown className="h-4 w-4" />
            Founder · Mentor · Believer in every girl
          </div>

          <blockquote className="mt-7 relative">
            <span className="absolute -top-6 -left-2 font-display text-7xl text-[#f6e27a]/40 leading-none">
              "
            </span>
            <p className="text-base sm:text-lg text-white/95 leading-relaxed pl-6">
              Lacey Fincher — wife, mother of three, certified life coach, master stylist, and former truck driver — founded
              <span className="font-semibold text-[#f6e27a]"> Queenz Like Me </span>
              to empower girls ages 6 to 18. Drawing from her own life journey, Lacey is committed to helping every girl discover
              the greatness already inside her.
            </p>
          </blockquote>

          <p className="mt-5 text-white/90 leading-relaxed text-sm sm:text-base">
            With a heart for youth and a passion for building confidence, self-worth, and resilience, Lacey creates a safe,
            supportive space where every girl feels seen, heard, and valued. Through mentorship, life coaching, leadership
            development, and life skills education, she guides girls to embrace their unique strengths and grow into confident,
            purpose-driven young women.
          </p>

          <p className="mt-4 text-white/95 leading-relaxed text-sm sm:text-base font-medium">
            Her mission is simple: <span className="text-[#f6e27a]">to ensure every girl knows that greatness already resides within her.</span>
          </p>

          <Link
            to="/register"
            data-testid="founder-cta"
            className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#f6e27a] text-[#4a1466] font-semibold hover:bg-white transition-colors"
          >
            Join Lacey's mission
            <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

/* ------------ Participate (Mentors / Volunteers / Partners) ------------ */
function Participate() {
  const opts = [
    {
      icon: GraduationCap,
      title: "Become a Mentor",
      blurb:
        "Pour into the next generation. Share your story, time, and wisdom with a young queen who's ready to grow.",
      cta: "Mentor with us",
      mailto:
        "mailto:info@queenzlikeme.com?subject=I'd%20like%20to%20mentor%20with%20Queenz%20Like%20Me",
      color: "#d62288",
      testid: "participate-mentor",
    },
    {
      icon: HeartHandshake,
      title: "Volunteer Your Time",
      blurb:
        "Help at events, workshops, and group sessions. Every hour you give helps a girl believe in herself.",
      cta: "Volunteer with us",
      mailto:
        "mailto:info@queenzlikeme.com?subject=I'd%20like%20to%20volunteer%20with%20Queenz%20Like%20Me",
      color: "#7d289b",
      testid: "participate-volunteer",
    },
    {
      icon: Handshake,
      title: "Partner With Us",
      blurb:
        "Schools, churches, and community orgs — let's build something powerful for the girls in our community.",
      cta: "Start a partnership",
      mailto:
        "mailto:info@queenzlikeme.com?subject=Partnership%20Inquiry%20-%20Queenz%20Like%20Me",
      color: "#d4af37",
      testid: "participate-partner",
    },
  ];

  return (
    <section
      id="participate"
      data-testid="participate-section"
      className="relative py-24 sm:py-32 magenta-gold-bg overflow-hidden"
    >
      <div className="absolute inset-0 gold-dots opacity-40 pointer-events-none" aria-hidden />
      <Sparkles className="absolute top-16 left-[8%] h-9 w-9 text-[#d4af37] floaty opacity-60" aria-hidden />
      <Heart className="absolute bottom-20 right-[7%] h-8 w-8 text-[#d62288] floaty-slow opacity-50" aria-hidden />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="text-center max-w-3xl mx-auto">
          <div className="crown-divider mb-6">
            <Crown className="h-5 w-5" />
          </div>
          <p className="font-script text-3xl text-[#7a5a14] drop-shadow-[0_1px_0_rgba(255,255,255,0.6)] mb-2">Get Involved</p>
          <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-[#4a1466]">
            Participate in the mission.
          </h2>
          <p className="mt-5 text-lg text-[#5c4a54]">
            It takes a village to raise a queen. Here's how you can help us
            build her up.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-7">
          {opts.map((o, i) => (
            <motion.div
              key={o.title}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              custom={i}
              variants={fade}
              data-testid={o.testid}
              className="relative rounded-3xl p-8 glass-card hover:-translate-y-2 transition-transform"
              style={{ borderTop: `4px solid ${o.color}` }}
            >
              <div
                className="h-14 w-14 rounded-2xl flex items-center justify-center"
                style={{
                  background: `${o.color}1a`,
                  border: `1.5px solid ${o.color}66`,
                }}
              >
                <o.icon className="h-7 w-7" style={{ color: o.color }} />
              </div>
              <h3 className="mt-5 font-display text-2xl font-bold text-[#1a0f14]">
                {o.title}
              </h3>
              <p className="mt-3 text-[#5c4a54] leading-relaxed">{o.blurb}</p>
              <a
                href={o.mailto}
                data-testid={`${o.testid}-link`}
                className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all hover:-translate-y-0.5"
                style={{
                  background: o.color,
                  color: "white",
                  boxShadow: `0 8px 20px -6px ${o.color}80`,
                }}
              >
                {o.cta}
                <ArrowRight className="h-4 w-4" />
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------ Sponsor ------------ */
export function Sponsor() {
  const tiers = [
    {
      package_id: "tier_50",
      name: "Crown Bearer",
      amount: 50,
      perks: ["Sponsor 1 queen's monthly supplies", "Thank-you note from a queen"],
      color: "#ffb6c1",
    },
    {
      package_id: "tier_250",
      name: "Royal Patron",
      amount: 250,
      perks: [
        "Sponsor 1 queen for a full quarter",
        "Logo on our community wall",
        "Invitation to graduation event",
      ],
      color: "#d62288",
      featured: true,
    },
    {
      package_id: "tier_1000",
      name: "Queen Maker",
      amount: 1000,
      perks: [
        "Sponsor an entire age group event",
        "Featured social media spotlight",
        "Founder's personal thank-you",
      ],
      color: "#d4af37",
    },
  ];

  const [frequency, setFrequency] = useState("one_time");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [loadingTier, setLoadingTier] = useState(null);
  const [donationStatus, setDonationStatus] = useState(null); // 'success' | 'cancel' | null

  // Poll for Stripe return
  useEffect(() => {
    const url = new URL(window.location.href);
    const flag = url.searchParams.get("donation");
    const sid = url.searchParams.get("session_id");
    if (flag === "cancel") {
      setDonationStatus("cancel");
      toast.info("Donation cancelled. No charge was made.");
      url.searchParams.delete("donation");
      window.history.replaceState({}, "", url.pathname + url.search + "#sponsor");
      return;
    }
    if (flag === "success" && sid) {
      setDonationStatus("polling");
      let attempts = 0;
      const max = 8;
      const poll = async () => {
        attempts += 1;
        try {
          const { data } = await axios.get(`${API}/donations/status/${sid}`);
          if (data.payment_status === "paid") {
            setDonationStatus("success");
            toast.success(`Thank you for your $${data.amount} gift ♕`);
            url.searchParams.delete("donation");
            url.searchParams.delete("session_id");
            window.history.replaceState({}, "", url.pathname + url.search + "#sponsor");
            return;
          }
          if (data.status === "expired" || attempts >= max) {
            setDonationStatus("expired");
            toast.error("We couldn't confirm payment. Please check your email.");
            return;
          }
          setTimeout(poll, 2000);
        } catch {
          if (attempts >= max) {
            setDonationStatus("expired");
            return;
          }
          setTimeout(poll, 2000);
        }
      };
      poll();
    }
  }, []);

  const donateTier = async (pkg) => {
    setLoadingTier(pkg.package_id);
    try {
      const { data } = await axios.post(`${API}/donations/checkout`, {
        package_id: pkg.package_id,
        frequency,
        donor_name: donorName.trim(),
        donor_email: donorEmail.trim() || null,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Unable to start checkout. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Checkout error");
      setLoadingTier(null);
    }
  };

  const donateCustom = async (e) => {
    e.preventDefault();
    const amt = parseFloat(customAmount);
    if (!amt || amt < 1) {
      toast.error("Please enter an amount of $1 or more.");
      return;
    }
    setLoadingTier("custom");
    try {
      const { data } = await axios.post(`${API}/donations/checkout/custom`, {
        amount: amt,
        frequency,
        donor_name: donorName.trim(),
        donor_email: donorEmail.trim() || null,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Unable to start checkout. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Checkout error");
      setLoadingTier(null);
    }
  };

  return (
    <section
      id="sponsor"
      data-testid="sponsor-section"
      className="relative py-24 sm:py-32 overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, #4a1466 0%, #7d289b 40%, #d62288 100%)",
      }}
    >
      <Sparkles className="absolute top-12 left-[6%] h-9 w-9 text-[#f6e27a]/40 floaty" />
      <Crown className="absolute bottom-16 right-[8%] h-12 w-12 text-[#f6e27a]/30 floaty-slow" />
      <div className="absolute inset-0 opacity-20 pointer-events-none gold-dots" aria-hidden />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="text-center max-w-3xl mx-auto text-white">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-[#f6e27a]/20 border border-[#f6e27a]/40 mb-5">
            <Gift className="h-6 w-6 text-[#f6e27a]" />
          </div>
          <p className="font-script text-3xl text-[#f6e27a] mb-2">Sponsor a Queen</p>
          <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight">
            Crown her future.
          </h2>
          <p className="mt-5 text-lg text-white/85">
            Your sponsorship fuels mentorship, supplies, events, and
            scholarships for girls who need it most. Every gift writes a piece
            of her story.
          </p>
        </div>

        {/* Success / cancel banners */}
        {donationStatus === "success" && (
          <div
            data-testid="donation-success-banner"
            className="mt-10 max-w-2xl mx-auto rounded-2xl bg-[#f6e27a] text-[#4a1466] px-6 py-5 text-center shadow-xl"
          >
            <Crown className="h-6 w-6 mx-auto mb-2" />
            <p className="font-display text-xl font-bold">
              Thank you for your gift ♕
            </p>
            <p className="text-sm mt-1">
              A receipt was sent to your email. Your generosity is crowning a queen.
            </p>
          </div>
        )}
        {donationStatus === "polling" && (
          <div className="mt-10 max-w-2xl mx-auto rounded-2xl bg-white/15 text-white px-6 py-5 text-center backdrop-blur">
            Confirming your payment…
          </div>
        )}

        {/* Frequency toggle + donor info */}
        <div className="mt-12 max-w-3xl mx-auto bg-white/10 backdrop-blur rounded-3xl border border-white/20 p-6 sm:p-7">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
            <span className="text-white/90 text-sm font-semibold uppercase tracking-wider">
              I want to give
            </span>
            <div
              role="tablist"
              className="inline-flex rounded-full p-1 bg-white/15 border border-white/25"
            >
              {[
                { id: "one_time", label: "One-time" },
                { id: "monthly", label: "Monthly" },
              ].map((f) => (
                <button
                  key={f.id}
                  type="button"
                  data-testid={`frequency-${f.id}`}
                  onClick={() => setFrequency(f.id)}
                  className={`px-5 py-1.5 rounded-full text-sm font-semibold transition-all ${
                    frequency === f.id
                      ? "bg-[#f6e27a] text-[#4a1466] shadow"
                      : "text-white/85 hover:text-white"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-5">
            <input
              type="text"
              data-testid="donor-name"
              placeholder="Your name (optional)"
              className="ql-input bg-white/95"
              value={donorName}
              onChange={(e) => setDonorName(e.target.value)}
            />
            <input
              type="email"
              data-testid="donor-email"
              placeholder="Email for receipt (optional)"
              className="ql-input bg-white/95"
              value={donorEmail}
              onChange={(e) => setDonorEmail(e.target.value)}
            />
          </div>
        </div>

        {/* Tier cards */}
        <div className="mt-10 grid md:grid-cols-3 gap-7">
          {tiers.map((t, i) => (
            <motion.div
              key={t.name}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              custom={i}
              variants={fade}
              data-testid={`sponsor-tier-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
              className={`relative rounded-3xl p-8 backdrop-blur transition-all hover:-translate-y-2 ${
                t.featured
                  ? "bg-white text-[#1a0f14] shadow-2xl ring-4 ring-[#f6e27a]"
                  : "bg-white/10 text-white border border-white/20"
              }`}
            >
              {t.featured && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-[#f6e27a] text-[#4a1466] font-display text-xs font-bold tracking-wider uppercase">
                  ♕ Most Loved
                </div>
              )}
              <div
                className="h-12 w-12 rounded-2xl flex items-center justify-center"
                style={{
                  background: t.featured ? `${t.color}22` : "rgba(246,226,122,0.18)",
                  border: `1.5px solid ${t.featured ? t.color : "rgba(246,226,122,0.5)"}`,
                }}
              >
                <Crown
                  className="h-6 w-6"
                  style={{ color: t.featured ? t.color : "#f6e27a" }}
                />
              </div>
              <h3 className="mt-5 font-display text-2xl font-bold">{t.name}</h3>
              <p
                className="mt-2 font-display text-4xl font-bold"
                style={{ color: t.featured ? t.color : "#f6e27a" }}
              >
                ${t.amount.toLocaleString()}
                <span
                  className={`text-base font-normal ml-1 ${
                    t.featured ? "text-[#5c4a54]" : "text-white/70"
                  }`}
                >
                  {frequency === "monthly" ? "/mo" : ""}
                </span>
              </p>
              <ul className="mt-5 space-y-2.5">
                {t.perks.map((p) => (
                  <li
                    key={p}
                    className={`flex items-start gap-2.5 text-sm ${
                      t.featured ? "text-[#1a0f14]" : "text-white/90"
                    }`}
                  >
                    <Check
                      className="h-4 w-4 mt-0.5 shrink-0"
                      style={{ color: t.featured ? t.color : "#f6e27a" }}
                    />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => donateTier(t)}
                disabled={loadingTier !== null}
                data-testid={`donate-${t.package_id}`}
                className={`mt-7 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold transition-all disabled:opacity-60 ${
                  t.featured
                    ? "bg-[#d62288] text-white hover:bg-[#b81a73]"
                    : "bg-[#f6e27a] text-[#4a1466] hover:bg-white"
                }`}
              >
                {loadingTier === t.package_id
                  ? "Redirecting…"
                  : `Donate $${t.amount.toLocaleString()}${frequency === "monthly" ? "/mo" : ""}`}
                {loadingTier !== t.package_id && <ArrowRight className="h-4 w-4" />}
              </button>
            </motion.div>
          ))}
        </div>

        {/* Custom amount */}
        <form
          onSubmit={donateCustom}
          data-testid="custom-donation-form"
          className="mt-10 max-w-3xl mx-auto bg-white rounded-3xl p-6 sm:p-7 shadow-2xl flex flex-col sm:flex-row items-stretch sm:items-end gap-4"
        >
          <div className="flex-1">
            <label className="ql-label">Give a custom amount</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7d289b] font-bold text-lg">
                $
              </span>
              <input
                type="number"
                min="1"
                max="50000"
                step="1"
                data-testid="custom-amount"
                className="ql-input pl-9 text-lg"
                placeholder="Enter amount"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
              />
            </div>
            <p className="mt-2 text-xs text-[#5c4a54]">
              {frequency === "monthly"
                ? "We'll start your recurring monthly gift."
                : "One-time gift."}{" "}
              Min $1, max $50,000.
            </p>
          </div>
          <button
            type="submit"
            disabled={loadingTier !== null}
            data-testid="donate-custom"
            className="btn-primary justify-center disabled:opacity-60"
          >
            {loadingTier === "custom" ? "Redirecting…" : "Donate custom amount"}
            {loadingTier !== "custom" && <Crown className="h-4 w-4" />}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-white/70 max-w-2xl mx-auto">
          Secure checkout powered by Stripe. Cards accepted globally. We'll
          never share your information.
        </p>

        <div className="mt-6 flex justify-center">
          <RulesModalTrigger
            testid="sponsor-rules-link"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-[#f6e27a] bg-white/10 text-[#f6e27a] font-semibold text-sm hover:bg-[#f6e27a] hover:text-[#4a1466] transition-all"
          >
            <Scroll className="h-4 w-4" />
            Read the Rules &amp; Regulations
          </RulesModalTrigger>
        </div>

        <OtherWaysToGive />

        <div className="mt-10 text-center text-white">
          <p className="text-white/85 max-w-2xl mx-auto text-sm">
            Prefer to talk first, donate items, or sponsor an event?
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-4">
            <a
              href="mailto:info@queenzlikeme.com?subject=I'd%20like%20to%20become%20a%20sponsor"
              data-testid="sponsor-cta-email"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/10 border border-white/30 text-white font-semibold hover:bg-white/20 transition-colors"
            >
              <Mail className="h-4 w-4" />
              Email us
            </a>
            <a
              href="tel:4484885772"
              data-testid="sponsor-cta-phone"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/10 border border-white/30 text-white font-semibold hover:bg-white/20 transition-colors"
            >
              <Phone className="h-4 w-4" />
              Call (448) 488-5772
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------ Waitlist ------------ */
export function Waitlist() {
  const [form, setForm] = useState({
    parent_name: "",
    parent_email: "",
    parent_phone: "",
    child_first_name: "",
    child_age: "",
    age_group: "",
    notes: "",
  });
  const [rulesAck, setRulesAck] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Allow other sections (Register) to push a prefill payload when cohort is full
  useEffect(() => {
    const handler = (e) => {
      const d = e.detail || {};
      setForm((prev) => ({
        ...prev,
        parent_name: d.parent_name || prev.parent_name,
        parent_email: d.parent_email || prev.parent_email,
        parent_phone: d.parent_phone || prev.parent_phone,
        child_first_name:
          (d.first_child && d.first_child.child_first_name) || prev.child_first_name,
        child_age:
          (d.first_child && d.first_child.child_age) || prev.child_age,
        age_group:
          (d.first_child && d.first_child.age_group) || prev.age_group,
      }));
      setSuccess(false);
    };
    window.addEventListener("ql:prefill-waitlist", handler);
    return () => window.removeEventListener("ql:prefill-waitlist", handler);
  }, []);

  const setChildAge = (val) => {
    const age = parseInt(val, 10);
    let g = form.age_group;
    if (!Number.isNaN(age)) {
      if (age >= 6 && age <= 10) g = "royal_crowns";
      else if (age >= 11 && age <= 14) g = "bold_queens";
      else if (age >= 15 && age <= 18) g = "fearless_queens";
    }
    setForm({ ...form, child_age: val, age_group: g });
  };
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!rulesAck) {
      toast.error("Please agree to the Rules & Regulations before submitting.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/waitlist`, {
        ...form,
        child_age: parseInt(form.child_age, 10),
        rules_acknowledged: true,
      });
      setSuccess(true);
      toast.success("You're on the list ♕ We'll reach out as soon as a spot opens!");
      setForm({
        parent_name: "",
        parent_email: "",
        parent_phone: "",
        child_first_name: "",
        child_age: "",
        age_group: "",
        notes: "",
      });
      setRulesAck(false);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="waitlist"
      data-testid="waitlist-section"
      className="relative py-24 sm:py-32 gold-bg overflow-hidden"
    >
      <div className="absolute inset-0 gold-dots opacity-35 pointer-events-none" aria-hidden />
      <Hourglass className="absolute top-16 right-[8%] h-9 w-9 text-[#d4af37] floaty opacity-60" aria-hidden />
      <Crown className="absolute bottom-20 left-[6%] h-10 w-10 text-[#d62288] floaty-slow opacity-40" aria-hidden />

      <div className="max-w-5xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-10 relative">
        <div className="lg:col-span-5">
          <div className="crown-divider mb-5 justify-start">
            <Crown className="h-5 w-5" />
          </div>
          <p className="font-script text-3xl text-[#7a5a14] drop-shadow-[0_1px_0_rgba(255,255,255,0.6)] mb-2">Cohort Filling Up</p>
          <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-[#4a1466] leading-tight">
            Join the waiting list.
          </h2>
          <p className="mt-5 text-white/95 leading-relaxed drop-shadow">
            Our current cohort is filling fast. Drop your details here and
            you'll be the first to know when a spot opens up — or when the next
            cohort kicks off.
          </p>
          <ul className="mt-6 space-y-3 text-sm text-white drop-shadow">
            <li className="flex items-center gap-2">
              <Check className="h-4 w-4 text-[#f6e27a]" />
              Priority notification when seats open
            </li>
            <li className="flex items-center gap-2">
              <Check className="h-4 w-4 text-[#f6e27a]" />
              Early access to events & meetups
            </li>
            <li className="flex items-center gap-2">
              <Check className="h-4 w-4 text-[#f6e27a]" />
              No spam — only the good stuff
            </li>
          </ul>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          onSubmit={submit}
          data-testid="waitlist-form"
          className="lg:col-span-7 glass-card rounded-3xl p-7 sm:p-9"
        >
          {success ? (
            <div data-testid="waitlist-success" className="text-center py-10">
              <div className="mx-auto h-16 w-16 rounded-full bg-[#d4af37]/20 flex items-center justify-center">
                <Hourglass className="h-8 w-8 text-[#d4af37]" />
              </div>
              <h3 className="mt-5 font-display text-3xl font-bold text-[#4a1466]">
                You're on the list!
              </h3>
              <p className="mt-3 text-[#5c4a54]">
                We'll be in touch the moment a seat opens. Welcome to the
                sisterhood-in-waiting.
              </p>
              <button
                type="button"
                onClick={() => setSuccess(false)}
                data-testid="waitlist-another"
                className="btn-outline-gold mt-7"
              >
                Add another queen
              </button>
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="ql-label">Parent / Guardian name</label>
                  <input
                    required
                    type="text"
                    data-testid="waitlist-parent-name"
                    className="ql-input"
                    placeholder="Your full name"
                    value={form.parent_name}
                    onChange={update("parent_name")}
                  />
                </div>
                <div>
                  <label className="ql-label">Email</label>
                  <input
                    required
                    type="email"
                    data-testid="waitlist-parent-email"
                    className="ql-input"
                    placeholder="you@example.com"
                    value={form.parent_email}
                    onChange={update("parent_email")}
                  />
                </div>
                <div>
                  <label className="ql-label">Phone (optional)</label>
                  <input
                    type="tel"
                    data-testid="waitlist-parent-phone"
                    className="ql-input"
                    placeholder="(555) 555-1234"
                    value={form.parent_phone}
                    onChange={update("parent_phone")}
                  />
                </div>
                <div>
                  <label className="ql-label">Daughter's first name</label>
                  <input
                    required
                    type="text"
                    data-testid="waitlist-child-name"
                    className="ql-input"
                    placeholder="Her name"
                    value={form.child_first_name}
                    onChange={update("child_first_name")}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="ql-label">Her age (6 – 18)</label>
                  <input
                    required
                    type="number"
                    min="6"
                    max="18"
                    data-testid="waitlist-child-age"
                    className="ql-input"
                    placeholder="Age"
                    value={form.child_age}
                    onChange={(e) => setChildAge(e.target.value)}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="ql-label">Anything else? (optional)</label>
                  <textarea
                    data-testid="waitlist-notes"
                    className="ql-input min-h-[90px] resize-y"
                    placeholder="Preferred meeting times, special requests, etc."
                    value={form.notes}
                    onChange={update("notes")}
                  />
                </div>
              </div>

              <div className="mt-7 rounded-2xl border-2 border-[#d4af37] bg-[#fff8e1] p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    required
                    data-testid="waitlist-rules-ack"
                    checked={rulesAck}
                    onChange={(e) => setRulesAck(e.target.checked)}
                    className="mt-1 h-5 w-5 accent-[#7d289b]"
                  />
                  <span className="text-sm text-[#1a0f14] leading-relaxed">
                    I have read and agree to the{" "}
                    <RulesModalTrigger
                      testid="waitlist-open-rules-from-checkbox"
                      className="font-semibold text-[#7d289b] underline hover:text-[#d62288]"
                    >
                      Queenz Like Me Rules &amp; Regulations
                    </RulesModalTrigger>
                    .
                  </span>
                </label>
              </div>

              <button
                type="submit"
                data-testid="waitlist-submit"
                disabled={loading}
                className="btn-primary w-full sm:w-auto mt-7 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Adding you…" : "Join the Waitlist"}
                {!loading && <Hourglass className="h-4 w-4" />}
              </button>
              <div className="mt-5 pt-5 border-t border-[#f0e1e8]">
                <RulesPillLink testid="waitlist-rules-link" />
              </div>
            </>
          )}
        </motion.form>
      </div>
    </section>
  );
}

/* ------------ Register ------------ */
function inferGroup(age) {
  if (age >= 6 && age <= 10) return "royal_crowns";
  if (age >= 11 && age <= 14) return "bold_queens";
  if (age >= 15 && age <= 18) return "fearless_queens";
  return "";
}

export function Register() {
  const [parent, setParent] = useState({
    parent_name: "",
    parent_email: "",
    parent_phone: "",
    message: "",
  });
  const [children, setChildren] = useState([
    { child_first_name: "", child_age: "", child_dob: "", age_group: "", shirt_size: "", allergies: "" },
  ]);
  const [rulesAck, setRulesAck] = useState(false);
  const [signatureName, setSignatureName] = useState("");
  const [rulesOpen, setRulesOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successCount, setSuccessCount] = useState(0);
  const [capacity, setCapacity] = useState(null);

  // Pre-fetch cohort capacity on mount and on success reset
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/registrations/capacity`)
      .then((r) => {
        if (!cancelled) setCapacity(r.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [success]);

  const updateParent = (k) => (e) =>
    setParent({ ...parent, [k]: e.target.value });

  const updateChild = (i, k, val) => {
    setChildren((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], [k]: val };
      if (k === "child_age") {
        next[i].age_group = inferGroup(parseInt(val, 10));
      }
      return next;
    });
  };

  const addChild = () =>
    setChildren((prev) => [
      ...prev,
      { child_first_name: "", child_age: "", child_dob: "", age_group: "", shirt_size: "", allergies: "" },
    ]);

  const removeChild = (i) =>
    setChildren((prev) => (prev.length === 1 ? prev : prev.filter((_, x) => x !== i)));

  const switchToWaitlist = () => {
    // Pre-fill the waitlist via global event
    window.dispatchEvent(
      new CustomEvent("ql:prefill-waitlist", {
        detail: {
          parent_name: parent.parent_name,
          parent_email: parent.parent_email,
          parent_phone: parent.parent_phone,
          first_child: children[0] || {},
        },
      })
    );
    setTimeout(() => {
      const el = document.getElementById("waitlist");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  const submit = async (e) => {
    e.preventDefault();
    // basic validation
    for (let i = 0; i < children.length; i++) {
      const c = children[i];
      if (!c.child_first_name || !c.child_age || !c.child_dob || !c.age_group) {
        toast.error(`Please complete details for queen #${i + 1}.`);
        return;
      }
    }
    if (!rulesAck || !signatureName.trim()) {
      toast.error(
        "The Rules & Regulations must be signed before submitting — please read them, type your name, and check the box."
      );
      setRulesOpen(true);
      return;
    }
    // Capacity check before submitting
    try {
      const cap = (await axios.get(`${API}/registrations/capacity`)).data;
      setCapacity(cap);
      if (cap.remaining < children.length) {
        toast.error(
          cap.remaining === 0
            ? "Cohort is full — switching you to the waiting list."
            : `Only ${cap.remaining} seat(s) left — we'll move you to the waiting list.`
        );
        switchToWaitlist();
        return;
      }
    } catch (_) {
      /* ignore capacity preflight errors and try anyway */
    }

    setLoading(true);
    try {
      for (const c of children) {
        await axios.post(`${API}/registrations`, {
          ...parent,
          child_first_name: c.child_first_name,
          child_age: parseInt(c.child_age, 10),
          child_dob: c.child_dob,
          age_group: c.age_group,
          shirt_size: c.shirt_size || "",
          allergies: c.allergies || "",
          rules_acknowledged: true,
          signature_name: signatureName.trim(),
        });
      }
      setSuccess(true);
      setSuccessCount(children.length);
      toast.success(
        children.length === 1
          ? "Welcome to the sisterhood! ♕ We'll be in touch soon."
          : `${children.length} queenz registered ♕ Welcome to the sisterhood!`
      );
      setParent({
        parent_name: "",
        parent_email: "",
        parent_phone: "",
        message: "",
      });
      setChildren([
        { child_first_name: "", child_age: "", child_dob: "", age_group: "", shirt_size: "", allergies: "" },
      ]);
      setRulesAck(false);
      setSignatureName("");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && (detail?.code === "cohort_full" || detail?.message)) {
        toast.error("Cohort is full — moving you to the waiting list.");
        switchToWaitlist();
        return;
      }
      const msg =
        detail?.[0]?.msg ||
        (typeof detail === "string" ? detail : null) ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your details.");
    } finally {
      setLoading(false);
    }
  };

  const isFull = capacity && capacity.full;

  return (
    <section
      id="register"
      data-testid="register-section"
      className="relative py-24 sm:py-32 register-bg overflow-hidden"
    >
      <Sparkles className="absolute top-16 left-[8%] h-8 w-8 text-[#d4af37] floaty opacity-60" aria-hidden />
      <Crown className="absolute bottom-20 right-[6%] h-9 w-9 text-[#d62288] floaty-slow opacity-40" aria-hidden />
      <div className="max-w-5xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-10 relative">
        <div className="lg:col-span-5">
          <div className="crown-divider mb-5 justify-start">
            <Crown className="h-5 w-5" />
          </div>
          <p className="font-script text-3xl text-[#7a5a14] drop-shadow-[0_1px_0_rgba(255,255,255,0.6)] mb-2">
            Join the Sisterhood!
          </p>
          <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-[#4a1466] leading-tight">
            Register today & let's build tomorrow's Queenz.
          </h2>
          <p className="mt-5 text-white/95 leading-relaxed drop-shadow">
            Got more than one queen? You can sign up multiple daughters in a
            single submission — just add another below.
          </p>

          <div className="mt-8 space-y-3 text-sm">
            <div className="flex items-center gap-3 text-white drop-shadow">
              <Phone className="h-4 w-4 text-[#f6e27a]" />
              <a href="tel:4484885772" className="font-medium hover:underline">
                (448) 488-5772
              </a>
            </div>
            <div className="flex items-center gap-3 text-white drop-shadow">
              <Mail className="h-4 w-4 text-[#f6e27a]" />
              <a
                href="mailto:info@queenzlikeme.com"
                className="font-medium hover:underline"
              >
                info@queenzlikeme.com
              </a>
            </div>
            <div className="flex items-center gap-3 text-white drop-shadow">
              <Globe className="h-4 w-4 text-[#f6e27a]" />
              <a
                href="https://www.queenzlikeme.com"
                target="_blank"
                rel="noreferrer"
                className="font-medium hover:underline"
              >
                www.queenzlikeme.com
              </a>
            </div>
          </div>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          onSubmit={submit}
          data-testid="register-form"
          className="lg:col-span-7 glass-card rounded-3xl p-7 sm:p-9"
        >
          {success ? (
            <div data-testid="register-success" className="text-center py-10">
              <div className="mx-auto h-16 w-16 rounded-full bg-[#d4af37]/20 flex items-center justify-center">
                <Crown className="h-8 w-8 text-[#d4af37]" />
              </div>
              <h3 className="mt-5 font-display text-3xl font-bold text-[#4a1466]">
                {successCount > 1
                  ? `${successCount} Queenz Welcomed!`
                  : "Welcome, Queen!"}
              </h3>
              <p className="mt-3 text-[#5c4a54]">
                {successCount > 1
                  ? "All your daughters are registered. We'll reach out shortly with next steps."
                  : "Your registration is in. We can't wait to meet you and your daughter. We'll reach out shortly with next steps."}
              </p>
              <button
                type="button"
                onClick={() => {
                  setSuccess(false);
                  setSuccessCount(0);
                }}
                data-testid="register-another"
                className="btn-outline-gold mt-7"
              >
                Register more queenz
              </button>
            </div>
          ) : (
            <>
              {/* Cohort-full banner (auto-redirect to waiting list) */}
              {isFull && (
                <div
                  data-testid="cohort-full-banner"
                  className="mb-6 rounded-2xl border-2 border-[#d4af37] bg-[#fff8e1] p-4 flex items-start gap-3"
                >
                  <Hourglass className="h-5 w-5 text-[#7d289b] mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="font-display font-bold text-[#4a1466]">
                      This cohort is full ({capacity?.current}/{capacity?.limit})
                    </p>
                    <p className="text-sm text-[#5c4a54] mt-1">
                      We'd love to have your queen — please join the waiting list and we'll
                      reach out the moment a seat opens.
                    </p>
                    <button
                      type="button"
                      data-testid="switch-to-waitlist"
                      onClick={switchToWaitlist}
                      className="mt-3 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#7d289b] text-white font-semibold text-sm hover:bg-[#4a1466] transition-colors"
                    >
                      Move me to the waiting list
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {!isFull && capacity && capacity.remaining <= 5 && capacity.remaining > 0 && (
                <div
                  data-testid="cohort-low-banner"
                  className="mb-6 rounded-2xl border-2 border-[#d62288] bg-[#fff4fa] p-3 text-sm text-[#4a1466]"
                >
                  <span className="font-semibold">
                    Only {capacity.remaining} seat{capacity.remaining === 1 ? "" : "s"} left
                  </span>{" "}
                  in this cohort — secure your queen's spot today.
                </div>
              )}

              {/* Light snack note */}
              <div className="mb-6 rounded-2xl bg-[#fff8e1] border-l-4 border-[#d4af37] px-4 py-3 text-sm text-[#5c4a54]">
                <span className="font-semibold text-[#4a1466]">Heads up:</span>{" "}
                a light snack will be provided during scheduled sessions —
                please share any allergies below so we can keep every queen safe.
              </div>

              {/* Parent block */}
              <p className="font-display text-sm font-semibold tracking-wider uppercase text-[#7d289b] mb-3">
                Parent / Guardian
              </p>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="ql-label">Your name</label>
                  <input
                    required
                    type="text"
                    data-testid="input-parent-name"
                    className="ql-input"
                    placeholder="e.g. Ashley Williams"
                    value={parent.parent_name}
                    onChange={updateParent("parent_name")}
                  />
                </div>
                <div>
                  <label className="ql-label">Email</label>
                  <input
                    required
                    type="email"
                    data-testid="input-parent-email"
                    className="ql-input"
                    placeholder="you@example.com"
                    value={parent.parent_email}
                    onChange={updateParent("parent_email")}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="ql-label">Phone</label>
                  <input
                    required
                    type="tel"
                    data-testid="input-parent-phone"
                    className="ql-input"
                    placeholder="(555) 555-1234"
                    value={parent.parent_phone}
                    onChange={updateParent("parent_phone")}
                  />
                </div>
              </div>

              {/* Children blocks */}
              <div className="mt-8 space-y-5">
                <p className="font-display text-sm font-semibold tracking-wider uppercase text-[#7d289b]">
                  Daughter{children.length > 1 ? "s" : ""}
                </p>
                {children.map((c, i) => (
                  <div
                    key={i}
                    data-testid={`child-block-${i}`}
                    className="relative rounded-2xl border-2 border-[#f0e1e8] p-5 bg-white/60"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#d62288]">
                        <Crown className="h-3.5 w-3.5 text-[#d4af37]" />
                        Queen #{i + 1}
                      </span>
                      {children.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeChild(i)}
                          data-testid={`remove-child-${i}`}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-[#5c4a54] hover:text-[#d62288]"
                        >
                          <X className="h-3.5 w-3.5" />
                          Remove
                        </button>
                      )}
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="ql-label">First name</label>
                        <input
                          required
                          type="text"
                          data-testid={`input-child-name-${i}`}
                          className="ql-input"
                          placeholder="Her name"
                          value={c.child_first_name}
                          onChange={(e) =>
                            updateChild(i, "child_first_name", e.target.value)
                          }
                        />
                      </div>
                      <div>
                        <label className="ql-label">Age (6 – 18)</label>
                        <input
                          required
                          type="number"
                          min="6"
                          max="18"
                          data-testid={`input-child-age-${i}`}
                          className="ql-input"
                          placeholder="Age"
                          value={c.child_age}
                          onChange={(e) =>
                            updateChild(i, "child_age", e.target.value)
                          }
                        />
                      </div>
                      <div>
                        <label className="ql-label">Date of birth</label>
                        <input
                          required
                          type="date"
                          data-testid={`input-child-dob-${i}`}
                          className="ql-input"
                          value={c.child_dob || ""}
                          onChange={(e) =>
                            updateChild(i, "child_dob", e.target.value)
                          }
                        />
                      </div>
                      <div>
                        <label className="ql-label">
                          T-shirt size{" "}
                          <span className="text-[#7c6470] font-normal">— e.g. Youth M, Adult S</span>
                        </label>
                        <input
                          type="text"
                          data-testid={`input-child-shirt-${i}`}
                          className="ql-input"
                          placeholder="Her shirt size"
                          value={c.shirt_size || ""}
                          onChange={(e) =>
                            updateChild(i, "shirt_size", e.target.value)
                          }
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="ql-label">Age group</label>
                        <div className="grid grid-cols-3 gap-2">
                          {TIERS.map((t) => {
                            const selected = c.age_group === t.id;
                            return (
                              <button
                                type="button"
                                key={t.id}
                                data-testid={`select-group-${i}-${t.id}`}
                                onClick={() =>
                                  updateChild(i, "age_group", t.id)
                                }
                                className="rounded-xl p-2.5 text-left border-2 transition-all"
                                style={{
                                  borderColor: selected ? t.color : "#f0e1e8",
                                  background: selected ? t.soft : "white",
                                  boxShadow: selected
                                    ? `0 0 0 3px ${t.color}22`
                                    : "none",
                                }}
                              >
                                <span
                                  className="text-[10px] font-bold uppercase tracking-wider"
                                  style={{ color: t.color }}
                                >
                                  {t.range}
                                </span>
                                <div className="font-display text-sm font-semibold text-[#1a0f14]">
                                  {t.name}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="sm:col-span-2">
                        <label className="ql-label">
                          Known allergies (food / environmental){" "}
                          <span className="text-[#7c6470] font-normal">— write "None" if none</span>
                        </label>
                        <input
                          type="text"
                          data-testid={`input-child-allergies-${i}`}
                          className="ql-input"
                          placeholder="e.g. Peanuts, dairy, latex — or 'None'"
                          value={c.allergies || ""}
                          onChange={(e) =>
                            updateChild(i, "allergies", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addChild}
                  data-testid="add-another-child"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-dashed border-[#d62288]/50 text-[#d62288] font-semibold text-sm hover:border-[#d62288] hover:bg-[#d62288]/5 transition-all"
                >
                  <Plus className="h-4 w-4" />
                  Add another queen
                </button>
              </div>

              <div className="mt-7">
                <label className="ql-label">
                  Anything you'd like us to know? (optional)
                </label>
                <textarea
                  data-testid="input-message"
                  className="ql-input min-h-[100px] resize-y"
                  placeholder="Tell us a little about your daughter(s) — interests, hopes, anything special…"
                  value={parent.message}
                  onChange={updateParent("message")}
                />
              </div>

              <div className="mt-7 rounded-2xl border-2 border-[#d4af37] bg-[#fff8e1] p-4">
                <p className="font-display text-sm font-bold text-[#4a1466] mb-3">
                  Sign the Rules &amp; Regulations{" "}
                  <span className="text-[#d62288]">*</span>
                </p>
                <p className="text-sm text-[#5c4a54] mb-3">
                  Please{" "}
                  <RulesModalTrigger
                    testid="register-open-rules-from-checkbox"
                    className="font-semibold text-[#7d289b] underline hover:text-[#d62288]"
                  >
                    read the Queenz Like Me Rules &amp; Regulations
                  </RulesModalTrigger>
                  , then sign below.
                </p>
                <label className="ql-label">
                  Type your full name as your digital signature
                </label>
                <input
                  type="text"
                  data-testid="register-signature-name"
                  className="ql-input font-script text-xl"
                  placeholder="e.g. Ashley Williams"
                  value={signatureName}
                  onChange={(e) => setSignatureName(e.target.value)}
                />
                <label className="mt-4 flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="register-rules-ack"
                    checked={rulesAck}
                    onChange={(e) => setRulesAck(e.target.checked)}
                    className="mt-1 h-5 w-5 accent-[#7d289b]"
                  />
                  <span className="text-sm text-[#1a0f14] leading-relaxed">
                    I have read and agree to the Queenz Like Me Rules &amp;
                    Regulations, and the signature above is my own.
                  </span>
                </label>
              </div>
              <RulesDialog open={rulesOpen} onOpenChange={setRulesOpen} />

              <button
                type="submit"
                data-testid="register-submit"
                disabled={loading || isFull}
                className="btn-primary w-full sm:w-auto mt-7 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading
                  ? "Sending…"
                  : isFull
                  ? "Cohort is full — use the waitlist"
                  : children.length > 1
                  ? `Register My ${children.length} Queenz`
                  : "Register My Queen"}
                {!loading && !isFull && <Crown className="h-4 w-4" />}
              </button>
              <p className="mt-3 text-xs text-[#7c6470]">
                By registering, you'll receive a follow-up from the Queenz Like
                Me team at the email and phone you provided.
              </p>
              <div className="mt-5 pt-5 border-t border-[#f0e1e8] flex flex-wrap gap-3">
                <Link
                  to="/agreement"
                  data-testid="register-agreement-link"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#7d289b] hover:text-[#d62288] transition-colors group"
                >
                  <Crown className="h-4 w-4 text-[#d4af37]" />
                  Read &amp; sign the Parent &amp; Participant Agreement
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
                <RulesPillLink testid="register-rules-link" />
              </div>
            </>
          )}
        </motion.form>
      </div>
    </section>
  );
}

/* ------------ Footer ------------ */
function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative pt-20 pb-10 text-white"
      style={{
        background:
          "linear-gradient(180deg, #4a1466 0%, #2d0a40 100%)",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid md:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <img
                src="/logo-sm.png"
                alt="Queenz Like Me"
                className="h-14 w-14 rounded-full ring-2 ring-[#f6e27a]/60 shadow-lg"
              />
              <div className="leading-none">
                <span className="font-script text-3xl gold-text">
                  Queen<span className="font-display not-italic">z</span>
                </span>
                <span className="font-display text-xl font-semibold text-white ml-1">
                  Like Me
                </span>
              </div>
            </div>
            <p className="mt-4 text-white/75 leading-relaxed">
              Building confident, courageous, and purposeful young Queenz —
              one girl, one story, one crown at a time.
            </p>
            <p className="mt-4 text-xs uppercase tracking-wider text-[#f6e27a]/80">
              Serving our community with love & purpose
            </p>
          </div>

          <div>
            <h4 className="font-display text-lg font-semibold text-[#f6e27a]">
              Contact
            </h4>
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-[#f6e27a]" />
                <a
                  href="tel:4484885772"
                  data-testid="footer-phone"
                  className="hover:text-[#f6e27a]"
                >
                  (448) 488-5772
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-[#f6e27a]" />
                <a
                  href="mailto:info@queenzlikeme.com"
                  data-testid="footer-email"
                  className="hover:text-[#f6e27a]"
                >
                  info@queenzlikeme.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Globe className="h-4 w-4 text-[#f6e27a]" />
                <a
                  href="https://www.queenzlikeme.com"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-website"
                  className="hover:text-[#f6e27a] font-semibold"
                >
                  www.queenzlikeme.com
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-display text-lg font-semibold text-[#f6e27a]">
              Follow the Sisterhood
            </h4>
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex items-center gap-3">
                <Facebook className="h-4 w-4 text-[#f6e27a]" />
                <a
                  href="https://facebook.com/Queenzlikeme"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-facebook"
                  className="hover:text-[#f6e27a]"
                >
                  Queenzlikeme
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Instagram className="h-4 w-4 text-[#f6e27a]" />
                <a
                  href="https://instagram.com/Queenzlikeme"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-instagram"
                  className="hover:text-[#f6e27a]"
                >
                  Queenzlikeme
                </a>
              </li>
              <li className="flex items-center gap-3">
                <svg
                  viewBox="0 0 24 24"
                  className="h-4 w-4 fill-[#f6e27a]"
                  aria-hidden
                >
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.62a8.16 8.16 0 0 0 4.77 1.52V6.69a4.84 4.84 0 0 1-1.84 0Z" />
                </svg>
                <a
                  href="https://tiktok.com/@Queenzlikeme"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-tiktok"
                  className="hover:text-[#f6e27a]"
                >
                  Queenzlikeme
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/15 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-5">
            <p className="text-xs text-white/60">
              © {new Date().getFullYear()} Queenz Like Me. All rights reserved.
            </p>
            <Link
              to="/agreement"
              data-testid="footer-agreement-link"
              className="text-xs font-semibold text-[#f6e27a] hover:text-white transition-colors underline-offset-4 hover:underline"
            >
              Participation Agreement
            </Link>
          </div>
          <p className="font-script text-lg gold-text">
            Building tomorrow's Queenz ♕
          </p>
        </div>
      </div>
    </footer>
  );
}

/* ------------ Page ------------ */
function Home() {
  useEffect(() => {
    // simple ping
    axios.get(`${API}/`).catch(() => {});
  }, []);
  return (
    <div data-testid="home-page">
      <Nav />
      <Hero />
      <QuickLinks />
      <Mission />
      <About />
      <Programs />
      <Focus />
      <Founder />
      <Participate />
      <Footer />
    </div>
  );
}

function RegisterRoute() {
  return (
    <PageLayout
      testid="register-page"
      shareMessage="Sign your queen up for Queenz Like Me — a girls mentoring program building confident, courageous queenz (ages 6–18)."
    >
      <Register />
    </PageLayout>
  );
}

function WaitlistRoute() {
  return (
    <PageLayout
      testid="waitlist-page"
      shareMessage="Join the waiting list for Queenz Like Me — a girls mentoring program (ages 6–18). New cohort filling up!"
    >
      <Waitlist />
    </PageLayout>
  );
}

function SponsorRoute() {
  return (
    <PageLayout
      testid="sponsor-page"
      shareMessage="Sponsor a girl through Queenz Like Me — every gift fuels mentorship, supplies, and scholarships for queenz who need it most."
    >
      <Sponsor />
    </PageLayout>
  );
}

function App() {
  return (
    <div className="App">
      <Toaster
        position="top-center"
        richColors
        toastOptions={{
          style: {
            fontFamily: "Outfit, sans-serif",
          },
        }}
      />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/register" element={<RegisterRoute />} />
          <Route path="/waitlist" element={<WaitlistRoute />} />
          <Route path="/sponsor" element={<SponsorRoute />} />
          <Route path="/rules" element={<RulesPage />} />
          <Route path="/agreement" element={<AgreementPage />} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
